import { useEffect, useReducer } from 'react';
import type { ComponentType } from 'react';

// Code splitting senza Suspense: le Suspense boundary non hanno marcatori
// nell'HTML prerenderizzato con Puppeteer e fanno fallire l'idratazione.
// Qui il chunk della route corrente viene precaricato PRIMA di hydrateRoot
// (vedi main.tsx), quindi al primo render il componente esiste già.

const loaders: Record<string, () => Promise<{ default: ComponentType }>> = {
  '/chi-sono': () => import('./pages/ChiSonoPage'),
  '/servizi': () => import('./pages/ServiziPage'),
  '/ai-consulting-services': () => import('./pages/AiConsultingServicesPage'),
  '/metodologia': () => import('./pages/MetodologiaPage'),
  '/newsletter': () => import('./pages/NewsletterPage'),
  '/conferma-iscrizione': () => import('./pages/NewsletterSubscription'),
  '/unsubscribe': () => import('./pages/NewsletterSubscription'),
  '/vibe-coder': () => import('./pages/VibeCoderPage'),
  '/testimonianze': () => import('./pages/TestimonianzePage'),
  '/archivio': () => import('./pages/Archivio'),
  '/numero/:numero': () => import('./pages/Numero'),
  '/privacy-policy': () => import('./pages/PrivacyPolicy'),
  '/cookie-policy': () => import('./pages/CookiePolicy'),
  '/extra': () => import('./pages/ExtraPage'),
  '/blog': () => import('./pages/BlogList'),
  '/blog/:slug': () => import('./pages/BlogPost'),
  '/umani-per-ora': () => import('./pages/UmaniPerOraPage'),
  '/press-kit-umani-per-ora': () => import('./pages/PressKitPage'),
  '/guida-ai-azienda': () => import('./pages/GuidaAiAziendaPage'),
  '/termini-servizio': () => import('./pages/TermsOfService'),
  // Catch-all: App.tsx la monta su path="*". Sta nel registry per poterla
  // precaricare prima dell'idratazione/del primo render.
  '/404': () => import('./pages/NotFound'),
};

const cache = new Map<string, ComponentType>();

// Deriva automaticamente i pattern parametrici dal registry, così ogni nuova
// route ':param' aggiunta a `loaders` viene riconosciuta da patternForPath
// senza dover toccare nient'altro (evita la regressione silenziosa segnalata
// nel code review 2026-08-29).
const parametricPatterns: Array<{ pattern: string; re: RegExp }> = Object.keys(
  loaders,
)
  .filter((p) => p.includes(':'))
  .map((pattern) => ({
    pattern,
    re: new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$'),
  }));

export function patternForPath(pathname: string): string | null {
  const p =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  if (loaders[p]) return p;
  for (const { pattern, re } of parametricPatterns) {
    if (re.test(p)) return pattern;
  }
  return null;
}

/**
 * Precarica il chunk della route corrente. Da chiamare prima del primo render.
 * Un path sconosciuto (non la home, che non passa da LazyPage) precarica la
 * pagina 404, così anche lì il primo render è sincrono.
 */
export async function preloadRoute(pathname: string): Promise<void> {
  const isRoot = pathname === '/' || pathname === '';
  const pattern = patternForPath(pathname) ?? (isRoot ? null : '/404');
  if (!pattern || cache.has(pattern)) return;
  const mod = await loaders[pattern]();
  cache.set(pattern, mod.default);
}

/**
 * Renderizza la pagina dal cache se pronta, altrimenti la carica
 * (navigazioni client-side).
 *
 * Nota: all'hydration iniziale il chunk è già in cache grazie a
 * preloadRoute() in main.tsx, quindi il render è sincrono. Per le
 * navigazioni client verso una route non ancora visitata, mostriamo un
 * placeholder che preserva l'altezza della viewport ed espone aria-busy,
 * così l'utente non vede un flash bianco né un salto di layout.
 */
export function LazyPage({ route }: { route: string }) {
  const [, force] = useReducer((x: number) => x + 1, 0);
  const C = cache.get(route);
  useEffect(() => {
    // Se l'utente naviga via prima che la promise si risolva, saltiamo il
    // force() per non aggiornare uno stato su un componente unmounted
    // (evita il warning React "state update on unmounted component").
    let cancelled = false;
    if (!cache.has(route)) {
      loaders[route]().then((m) => {
        if (cancelled) return;
        cache.set(route, m.default);
        force();
      });
    }
    return () => {
      cancelled = true;
    };
  }, [route]);
  if (C) return <C />;
  return <div aria-busy="true" style={{ minHeight: '60vh' }} />;
}
